import React, { ReactNode, ErrorInfo } from "react";

interface Props {
  children?: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    this.setState({ errorInfo });
  }

  private handleReset = () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch {}
    window.location.href = window.location.origin + window.location.pathname;
  };

  private handleContinue = () => {
    if (
      this.state.error?.message?.toLowerCase().includes('fetch dynamically imported module') ||
      this.state.error?.message?.toLowerCase().includes('failed to fetch')
    ) {
      window.location.reload();
      return;
    }
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-slate-900 text-white flex flex-col items-center justify-center p-6 text-center font-sans">
          <div className="max-w-md w-full bg-slate-800 border border-slate-700 rounded-2xl p-8 shadow-2xl">
            <div className="w-12 h-12 rounded-full bg-indigo-500/20 text-indigo-400 flex items-center justify-center mx-auto mb-4 text-xl font-bold">
              HD
            </div>
            <h2 className="text-xl font-bold text-slate-100 mb-2">HarryDesk Portal</h2>
            <p className="text-sm text-slate-400 mb-6 leading-relaxed">
              We recovered from a minor interface refresh. Click below to continue browsing.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={this.handleContinue}
                className="w-full py-2.5 px-4 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-sm rounded-xl transition cursor-pointer"
              >
                Continue to HarryDesk
              </button>
              <button
                onClick={this.handleReset}
                className="w-full py-2 px-4 bg-slate-700 hover:bg-slate-600 text-slate-300 font-medium text-xs rounded-xl transition cursor-pointer"
              >
                Refresh View
              </button>
            </div>
            {this.state.error && (
              <div className="mt-6 text-left p-3 bg-slate-950/80 rounded-lg text-xs text-slate-400 font-mono overflow-auto max-h-32">
                {this.state.error.message}
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children || null;
  }
}

